// SignalTooltip.tsx
// Reusable tooltip that shows full signal metadata on hover.
// Wrap any element: <SignalTooltip signal={sig}><Badge>...</Badge></SignalTooltip>

import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import type { SignalItem } from '../api/types';
import {
    TRADER_GROUP_COLORS,
    TRADER_GROUP_LABELS,
    SIGNAL_TYPE_LABELS,
} from './signalConstants';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SignalTooltipProps {
    signal: SignalItem;
    children: React.ReactElement;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatSignalDate(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        + ' ' + d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' });
}

function isNegative(v: unknown): boolean {
    if (typeof v === 'number') return v < 0;
    if (typeof v === 'string') return v.startsWith('-');
    return false;
}

function isPositive(v: unknown): boolean {
    if (typeof v === 'number') return v > 0;
    if (typeof v === 'string') return v.startsWith('+');
    return false;
}

function formatValue(v: unknown): string {
    if (typeof v === 'number') {
        const abs = Math.abs(v);
        if (abs >= 1000) return v.toLocaleString('en-US', { maximumFractionDigits: 0 });
        if (abs >= 1) return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
        return v.toFixed(4);
    }
    return String(v ?? '—');
}

// ─── Section renderer ────────────────────────────────────────────────────────

function SignalSection({ label, data }: { label: string; data: Record<string, unknown> }) {
    const entries = Object.entries(data);
    if (entries.length === 0) return null;

    return (
        <Box sx={{ px: 1.5, py: 0.75, borderTop: 1, borderColor: 'rgba(255,255,255,0.04)' }}>
            <Typography sx={{
                fontSize: '8px', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.08em', color: 'text.disabled', mb: 0.5,
            }}>
                {label}
            </Typography>
            {entries.map(([k, v]) => (
                <Box key={k} sx={{ display: 'flex', gap: 0.75, mb: '2px' }}>
                    <Typography sx={{
                        fontSize: '10px', fontFamily: 'monospace',
                        color: 'text.disabled', minWidth: 80, flexShrink: 0,
                    }}>
                        {k.replace(/_/g, ' ')}
                    </Typography>
                    <Typography sx={{
                        fontSize: '10px', fontFamily: 'monospace',
                        wordBreak: 'break-word',
                        color: isNegative(v) ? 'error.main'
                            : isPositive(v) ? 'success.main'
                            : 'text.primary',
                    }}>
                        {formatValue(v)}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

// ─── Tooltip content ─────────────────────────────────────────────────────────

function SignalTooltipContent({ signal }: { signal: SignalItem }) {
    const theme = useTheme();
    const groupColor = TRADER_GROUP_COLORS[signal.trader_group] ?? '#6b7280';
    const typeLabel = SIGNAL_TYPE_LABELS[signal.signal_type] ?? signal.signal_type;
    const groupLabel = TRADER_GROUP_LABELS[signal.trader_group] ?? signal.trader_group;
    const isOpen = signal.status === 'open';

    const dirIcon = signal.direction === 'bullish'
        ? <TrendingUpIcon sx={{ fontSize: 14, color: 'success.main' }} />
        : signal.direction === 'bearish'
            ? <TrendingDownIcon sx={{ fontSize: 14, color: 'error.main' }} />
            : <RemoveIcon sx={{ fontSize: 14, color: 'text.disabled' }} />;

    const scoreColor = signal.direction === 'bullish' ? theme.palette.success.main
        : signal.direction === 'bearish' ? theme.palette.error.main
        : theme.palette.text.disabled;

    const sections = [
        { label: 'Fact', data: signal.fact },
        { label: 'Significance', data: signal.significance },
        { label: 'Validation', data: signal.validation },
        { label: 'Interpretation', data: signal.interpretation },
        { label: 'Session', data: signal.session },
    ].filter(s => s.data && Object.keys(s.data).length > 0);

    return (
        <Box sx={{ minWidth: 240, maxWidth: 300 }}>
            {/* Header */}
            <Box sx={{ px: 1.5, pt: 1.25, pb: 1, borderBottom: 1, borderColor: 'rgba(255,255,255,0.06)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    {dirIcon}
                    <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                        {typeLabel}
                    </Typography>
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{
                            width: 5, height: 5, borderRadius: '50%',
                            bgcolor: isOpen ? 'success.main' : 'text.disabled',
                        }} />
                        <Typography sx={{
                            fontSize: '9px', fontFamily: 'monospace',
                            color: isOpen ? 'success.main' : 'text.disabled',
                        }}>
                            {isOpen ? 'open' : 'closed'}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: '3px',
                        px: 0.5, py: '1px', borderRadius: '3px',
                        bgcolor: alpha(groupColor, 0.15),
                    }}>
                        <Box sx={{
                            width: 5, height: 5, borderRadius: '50%',
                            bgcolor: groupColor,
                        }} />
                        <Typography sx={{ fontSize: '9px', fontWeight: 600, color: groupColor }}>
                            {groupLabel}
                        </Typography>
                    </Box>
                    <Typography sx={{
                        fontSize: '10px', fontWeight: 600,
                        color: signal.direction === 'bullish' ? 'success.main'
                            : signal.direction === 'bearish' ? 'error.main'
                            : 'text.disabled',
                    }}>
                        {signal.direction ?? 'neutral'}
                    </Typography>
                    {/* Score bar */}
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{
                            width: 40, height: 3, borderRadius: 2,
                            bgcolor: alpha(theme.palette.text.primary, 0.08),
                            overflow: 'hidden',
                        }}>
                            <Box sx={{
                                width: `${Math.round(signal.score * 100)}%`,
                                height: '100%', borderRadius: 2,
                                bgcolor: scoreColor,
                            }} />
                        </Box>
                        <Typography sx={{
                            fontSize: '10px', fontFamily: 'monospace',
                            fontWeight: 600, color: 'text.secondary',
                        }}>
                            {signal.score.toFixed(2)}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Sections */}
            {sections.map(({ label, data }) => (
                <SignalSection key={label} label={label} data={data} />
            ))}

            {/* Footer */}
            <Box sx={{
                px: 1.5, py: 0.75,
                borderTop: 1, borderColor: 'rgba(255,255,255,0.06)',
                display: 'flex', alignItems: 'center', gap: 0.75,
                fontSize: '9px', fontFamily: 'monospace', color: 'text.disabled',
            }}>
                <Box sx={{
                    px: 0.5, py: '1px', borderRadius: '2px',
                    bgcolor: alpha(theme.palette.text.primary, 0.04),
                }}>
                    {signal.lifecycle}
                </Box>
                <Typography sx={{ fontSize: '9px', fontFamily: 'monospace', color: 'text.disabled' }}>
                    {signal.closed_at
                        ? `${formatSignalDate(signal.opened_at)} → ${formatSignalDate(signal.closed_at)}`
                        : `opened ${formatSignalDate(signal.opened_at)}`}
                </Typography>
            </Box>
        </Box>
    );
}

// ─── Public component ────────────────────────────────────────────────────────

export function SignalTooltip({ signal, children }: SignalTooltipProps) {
    return (
        <Tooltip
            title={<SignalTooltipContent signal={signal} />}
            enterDelay={400}
            leaveDelay={200}
            placement="right"
            arrow
            slotProps={{
                tooltip: {
                    sx: {
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                        border: 1,
                        borderColor: 'divider',
                        boxShadow: 8,
                        maxWidth: 340,
                        p: 0,
                    },
                },
                arrow: {
                    sx: { color: 'background.paper' },
                },
                popper: {
                    sx: { pointerEvents: 'auto' },
                },
            }}
        >
            {children}
        </Tooltip>
    );
}
